// The selected watch: its headline and what it looks at, the lifecycle
// controls, the busy / failed banners, the framed readout, what Merlin is
// honouring and the run history. Refining happens in the chat beside it
// (kanban 14927fad), so the panel carries no refine box; `refineSlot` is where
// a page can point at that chat.
//
// kanban f967fdaf (PJ's first read, 2026-11-02): the head leads with a
// one-line headline and a short "Looks at" list, the subject sentence sits
// behind "In your words", the readout's verdict is clamped to two lines, and
// the daily briefing is laid out as the aggregate it is: a filled head and an
// "Across your watches" strip of cards, one per other watch, each opening it.
import { useState, type ReactNode } from 'react';
import {
  CADENCE_OPTIONS,
  acrossWatches,
  lastFailure,
  relativeTime,
  runStatusLabel,
  triggerLabel,
  watchStatusLine,
} from '../../pages/merlinWatchesUtils';
import type { Watch, WatchCadence, WatchDetail } from '../../services/merlinWatches';
import { signalsReportsApi } from '../../services/signalsReports';
import styles from './watches.module.css';

/** A verdict longer than this gets the two-line clamp with a More toggle. */
const VERDICT_CLAMP_CHARS = 140;

export interface WatchPanelProps {
  detail: WatchDetail;
  html: string | null;
  isOwner: boolean;
  busy: boolean;
  showOwner?: boolean;
  onCadence: (cadence: WatchCadence) => void;
  onRunNow: () => void;
  onTogglePause: () => void;
  onRemove: () => Promise<void>;
  /** Rendered under the readout, beside the run history. */
  refineSlot?: ReactNode;
  /** The reader's other watches (rail order): a daily briefing shows them as
   * its "Across your watches" strip, one card each, and `onSelectWatch`
   * opens the one clicked. */
  siblings?: Watch[];
  onSelectWatch?: (id: string) => void;
}

function firstName(name: string | null): string {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first;
}

export function WatchPanel({
  detail,
  html,
  isOwner,
  busy,
  showOwner = false,
  onCadence,
  onRunNow,
  onTogglePause,
  onRemove,
  refineSlot,
  siblings = [],
  onSelectWatch,
}: WatchPanelProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [verdictOpen, setVerdictOpen] = useState(false);
  const w = detail.watch;
  const failure = lastFailure(detail.runs);
  const isBriefing = w.kind === 'briefing';
  const across = isBriefing ? acrossWatches(w, siblings) : [];
  const verdict = w.latest?.summary?.trim() ?? '';
  const verdictLong = verdict.length > VERDICT_CLAMP_CHARS;

  const remove = async () => {
    await onRemove();
    setConfirmRemove(false);
  };

  const actions = (
    <div className={styles.actions}>
      <select
        className={styles.select}
        value={w.cadence}
        onChange={(e) => onCadence(e.target.value as WatchCadence)}
        disabled={busy || w.status === 'removed' || !isOwner}
        aria-label="Cadence"
        data-action="watch_cadence_change"
        data-record-value="cadence"
        data-outcome="request"
      >
        {CADENCE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={onRunNow}
        disabled={busy || !!w.open_run || w.status === 'removed' || !isOwner}
        title="Ask Merlin to rewrite the readout now (a few minutes)."
        data-action="watch_check_now"
        data-outcome="request"
      >
        {w.open_run ? 'Merlin is checking…' : 'Check now'}
      </button>
      {w.status !== 'removed' && (
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onTogglePause}
          disabled={busy || !isOwner}
          data-action={w.status === 'paused' ? 'watch_resume' : 'watch_pause'}
          data-outcome="request"
        >
          {w.status === 'paused' ? 'Resume' : 'Pause'}
        </button>
      )}
      {w.status !== 'removed' && !confirmRemove && (
        <button
          type="button"
          className={styles.dangerBtn}
          onClick={() => setConfirmRemove(true)}
          disabled={busy || !isOwner}
          data-action="watch_remove"
          data-outcome="open"
        >
          {isBriefing ? 'Stop the briefing' : 'Remove watch'}
        </button>
      )}
      {confirmRemove && (
        <span className={styles.confirm}>
          Stop watching this?{' '}
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={() => void remove()}
            disabled={busy}
            data-action="watch_remove_confirm"
            data-outcome="request"
          >
            Yes, remove
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setConfirmRemove(false)}
            data-action="watch_remove_cancel"
            data-outcome="none"
          >
            Keep
          </button>
        </span>
      )}
    </div>
  );

  const metaLine = (
    <div className={styles.detailMeta}>
      {showOwner && w.owner_name ? `${w.owner_name} · ` : ''}
      {w.run_count} readout{w.run_count === 1 ? '' : 's'} · {watchStatusLine(w)}
    </div>
  );

  return (
    <div
      data-testid="watch-detail"
      data-target-watch-id={w.id}
      data-kind={w.kind}
      className={isBriefing ? styles.briefingPanel : undefined}
    >
      {isBriefing ? (
        <header className={styles.briefHead} data-testid="briefing-head">
          <div className={styles.briefHeadRow}>
            <div>
              <span className={styles.briefEyebrow}>Daily briefing · weekday mornings</span>
              <h2 className={styles.briefTitle}>
                {showOwner && w.owner_name
                  ? `${firstName(w.owner_name)}’s day at a glance`
                  : 'Your day at a glance'}
              </h2>
              <p className={styles.briefLead}>
                One read across everything Merlin watches for you: what moved, what needs you, and
                the numbers behind it.
              </p>
              {metaLine}
            </div>
            {actions}
          </div>
          {across.length > 0 && (
            <div className={styles.across} data-testid="briefing-across">
              <div className={styles.acrossLabel}>Across your watches</div>
              <div className={styles.acrossGrid}>
                {across.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={styles.acrossCard}
                    onClick={() => onSelectWatch?.(s.id)}
                    title={s.subject}
                    data-action="briefing_watch_card"
                    data-target-watch-id={s.id}
                    data-outcome="open"
                  >
                    <span className={styles.acrossHeadline}>{s.headline}</span>
                    <span className={styles.acrossGlance}>
                      {s.at_a_glance[0] ?? (s.latest ? s.latest.summary ?? '' : 'No readout yet')}
                    </span>
                    <span className={styles.acrossMeta}>{watchStatusLine(s)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </header>
      ) : (
        <div className={styles.detailHead}>
          <div className={styles.headText}>
            <h2 className={styles.subject}>{w.headline}</h2>
            {w.looks_at.length > 0 && (
              <ul className={styles.looksAt} aria-label="What this watch looks at" data-testid="watch-looks-at">
                {w.looks_at.map((line, i) => (
                  <li key={`${i}-${line}`}>{line}</li>
                ))}
              </ul>
            )}
            {w.subject.trim() !== w.headline.trim() && (
              <details className={styles.inYourWords} data-testid="watch-in-your-words">
                <summary data-action="watch_subject_toggle" data-outcome="open">
                  In your words
                </summary>
                <p>{w.subject}</p>
              </details>
            )}
            {metaLine}
          </div>
          {actions}
        </div>
      )}

      {w.open_run && (
        <div className={styles.busyBanner} data-testid="watch-busy">
          Merlin is {w.open_run.status === 'running' ? 'checking now' : 'queued to check'} (
          {triggerLabel(w.open_run.trigger)}). The readout updates here by itself — a few minutes.
        </div>
      )}
      {failure && !w.open_run && (
        <div className={styles.failBanner} data-testid="watch-failed">
          The last check failed ({relativeTime(failure.finished_at ?? failure.queued_at)}):{' '}
          {failure.error || 'no readout was published'}. The schedule is unchanged; press{' '}
          <em>Check now</em> to retry.
        </div>
      )}

      <div className={styles.reportWrap}>
        {w.latest ? (
          <>
            <div className={styles.reportHead}>
              <div className={styles.reportHeadText}>
                <div className={styles.reportTitle}>{w.latest.title}</div>
                <div className={styles.reportMeta}>
                  Readout {w.run_count} · {relativeTime(w.latest.created_at)}
                </div>
                {verdict && (
                  <div className={styles.verdictRow}>
                    <p
                      className={`${styles.verdict} ${verdictLong && !verdictOpen ? styles.verdictClamp : ''}`}
                      data-testid="readout-verdict"
                    >
                      {verdict}
                    </p>
                    {verdictLong && (
                      <button
                        type="button"
                        className={styles.moreBtn}
                        onClick={() => setVerdictOpen((v) => !v)}
                        aria-expanded={verdictOpen}
                        data-action="readout_verdict_toggle"
                        data-outcome="open"
                      >
                        {verdictOpen ? 'Less' : 'More'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <a
                className={styles.openLink}
                href={signalsReportsApi.viewPath(w.latest.id)}
                target="_blank"
                rel="noopener noreferrer"
                data-action="watch_open_full"
                data-outcome="navigate"
              >
                Open full page ↗
              </a>
            </div>
            {html === null ? (
              <div className={styles.muted}>Loading readout…</div>
            ) : (
              <iframe
                className={styles.frame}
                title={isBriefing ? 'Daily briefing' : 'Watch readout'}
                srcDoc={html}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
              />
            )}
          </>
        ) : (
          <div className={styles.emptyReport}>
            <div className={styles.emptyTitle}>No readout yet</div>
            {w.open_run
              ? 'Merlin is writing the first one now.'
              : 'Press Check now and Merlin writes the first one.'}
          </div>
        )}
      </div>

      <div className={styles.lower}>
        <section className={styles.honouring} aria-label="What Merlin is honouring">
          <h3>What Merlin is honouring</h3>
          {detail.refinements.length > 0 ? (
            <ol className={styles.refinements}>
              {detail.refinements.map((r, i) => (
                <li key={`${i}-${r}`}>{r}</li>
              ))}
            </ol>
          ) : (
            <p className={styles.hint}>Nothing yet beyond the subject itself.</p>
          )}
          {refineSlot}
        </section>

        <section className={styles.history} aria-label="Run history">
          <h3>Checks</h3>
          <ul className={styles.runList}>
            {detail.runs.map((r) => (
              <li key={r.id} className={styles.runItem}>
                <span className={`${styles.runStatus} ${styles[`run_${r.status}`] ?? ''}`}>
                  {runStatusLabel(r.status)}
                </span>
                <span className={styles.runMeta}>
                  {triggerLabel(r.trigger)} · {relativeTime(r.finished_at ?? r.started_at ?? r.queued_at)}
                  {r.refinement ? ` · “${r.refinement}”` : ''}
                  {r.error ? ` · ${r.error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
